import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";

class Carousels extends Component {
  state = {
    carousels: [],
    formData: { 
      id: null, 
      carousel_name: "", 
      carousel_status: "", 
      carousel_img: null,
      carousel_url: ""
    },
    imagePreview: "",
    deleteId: null,
    error: "",
    loading: false,
    submitting: false,
    modalMode: "add",
  };

  componentDidMount() {
    this.fetchCarousels();

    const modalEl = document.getElementById("carouselModal");
    if (modalEl) {
      modalEl.addEventListener("hidden.bs.modal", () => {
        this.setState({
          formData: { 
            id: null, 
            carousel_name: "", 
            carousel_status: "", 
            carousel_img: null,
            carousel_url: "" // reset new field
          },
          imagePreview: "",
          error: "",
          submitting: false,
          modalMode: "add",
        });
      });
    }
  }

  /** Fetch all carousels */
  fetchCarousels = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const res = await axios.get("/carousels");
      const data = res.data?.data || [];
      this.setState({ carousels: Array.isArray(data) ? data : [] });
    } catch (err) {
      this.setState({
        error: err.response?.data?.message || "Failed to load carousels.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /** Handle input field changes */
  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      formData: { ...prev.formData, [name]: value },
    }));
  };

  /** Handle file input change */
  handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      this.setState((prev) => ({
        formData: { ...prev.formData, carousel_img: file },
        imagePreview: URL.createObjectURL(file),
      }));
    }
  };

  /** Open modal (add or edit) */
  openModal = (mode = "add", carousel = null) => {
    const modal = new window.bootstrap.Modal(document.getElementById("carouselModal"));
    const baseUrl = axios.defaults.baseURL.replace("/api", "");

    if (mode === "edit" && carousel) {
      this.setState({
        modalMode: "edit",
        formData: {
          id: carousel.id,
          carousel_name: carousel.carousel_name,
          carousel_status: carousel.carousel_status?.toString() ?? "1",
          carousel_img: null,
          carousel_url: carousel.carousel_url || "" // populate new field
        },
        imagePreview: carousel.carousel_img
          ? `${baseUrl}/uploads/carousels/${carousel.carousel_img}`
          : "",
        error: "",
      });
    } else {
      this.setState({
        modalMode: "add",
        formData: { 
          id: null, 
          carousel_name: "", 
          carousel_status: "", 
          carousel_img: null,
          carousel_url: "" 
        },
        imagePreview: "",
        error: "",
      });
    }

    modal.show();
  };

  /** Submit form (add or update) */
  handleSubmit = async (e) => {
    e.preventDefault();
    const { formData, modalMode } = this.state;

    if (!formData.carousel_name) {
      return this.setState({ error: "Carousel name is required." });
    }

    if (formData.carousel_status === "") {
      return this.setState({ error: "Carousel status is required." });
    }

    const data = new FormData();
    data.append("carousel_name", formData.carousel_name);
    data.append("carousel_status", formData.carousel_status);
    data.append("carousel_url", formData.carousel_url); // append new field
    if (formData.carousel_img) data.append("carousel_img", formData.carousel_img);

    this.setState({ submitting: true, error: "" });

    try {
      const token = localStorage.getItem("token");

      if (modalMode === "add") {
        await axios.post("/carousels", data, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        await axios.post(`/carousels/${formData.id}?_method=PUT`, data, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      }

      await this.fetchCarousels();

      const modal = window.bootstrap.Modal.getInstance(document.getElementById("carouselModal"));
      modal.hide();

      Swal.fire({
        icon: "success",
        title:
          modalMode === "add"
            ? "Carousel added successfully!"
            : "Carousel updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      console.error(err);
      this.setState({
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Error saving carousel.",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  /** Confirm delete popup */
  confirmDelete = (id) => {
    this.setState({ deleteId: id });
    const modal = new window.bootstrap.Modal(document.getElementById("deleteModal"));
    modal.show();
  };

  /** Delete carousel */
  handleDelete = async () => {
    const { deleteId } = this.state;
    if (!deleteId) return;

    try {
      await axios.delete(`/carousels/${deleteId}`);
      await this.fetchCarousels();

      const modal = window.bootstrap.Modal.getInstance(document.getElementById("deleteModal"));
      modal.hide();

      Swal.fire({
        icon: "success",
        title: "Carousel deleted successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Failed to delete carousel.",
      });
    }
  };

  render() {
    const { carousels, formData, imagePreview, error, loading, submitting, modalMode } = this.state;
    const baseUrl = axios.defaults.baseURL.replace("/api", "");

    return (
      <div className="pc-container">
        <div className="pc-content">
          <div className="row">
            <div className="col-lg-12">
              <div className="card shadow-none">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <ul className="breadcrumb mb-0">
                    <li className="breadcrumb-item">Carousel</li>
                    <li className="breadcrumb-item active">List</li>
                  </ul>
                  <button
                    type="button"
                    className="btn btn-light-warning"
                    onClick={() => this.openModal("add")}
                  >
                    + New Carousel
                  </button>
                </div>

                <div className="card-body">
                  {error && <div className="alert alert-danger">{error}</div>}
                  {loading ? (
                    <div className="text-center py-4">Loading...</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Tital 1</th>
                            <th>Tital 2</th>
                            <th>Image</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {carousels.length ? (
                            carousels.map((item, index) => (
                              <tr key={item.id}>
                                <td>{index + 1}</td>
                                <td>{item.carousel_name}</td>
                                <td>{item.carousel_url || "-"}</td> 
                                <td>
                                  {item.carousel_img ? (
                                    <img
                                      src={`${baseUrl}/uploads/carousels/${item.carousel_img}`}
                                      alt="img"
                                      style={{ width: "50px", height: "50px", borderRadius: "6px" }}
                                    />
                                  ) : (
                                    "No Image"
                                  )}
                                </td>
                                <td>
                                  {item.carousel_status === "1" || item.carousel_status === 1 ? (
                                    <span className="badge bg-success">Active</span>
                                  ) : (
                                    <span className="badge bg-danger">Inactive</span>
                                  )}
                                </td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-light-success me-2"
                                    onClick={() => this.openModal("edit", item)}
                                  >
                                    <i className="ph ph-note-pencil"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-light-danger"
                                    onClick={() => this.confirmDelete(item.id)}
                                  >
                                    <i className="ph ph-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center">
                                No carousels found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        <div className="modal fade" id="carouselModal" tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={this.handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    {modalMode === "add" ? "Add Carousel" : "Edit Carousel"}
                  </h5>
                  <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                </div>

                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Tital 1</label>
                    <input
                      type="text"
                      name="carousel_name"
                      className="form-control"
                      value={formData.carousel_name}
                      onChange={this.handleChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Tital 2</label> {/* new field */}
                    <input
                      type="text"
                      name="carousel_url"
                      className="form-control"
                      value={formData.carousel_url}
                      onChange={this.handleChange}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      name="carousel_status"
                      className="form-select"
                      value={formData.carousel_status}
                      onChange={this.handleChange}
                      required
                    >
                      <option value="">Select Status</option>
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Image</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={this.handleFileChange}
                    />
                    {imagePreview && (
                      <div className="mt-2 text-center">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{ width: "100px", borderRadius: "8px" }}
                        />
                      </div>
                    )}
                  </div>

                  {error && <div className="alert alert-danger">{error}</div>}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light-danger"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-light-primary"
                    disabled={submitting}
                  >
                    {submitting
                      ? "Saving..."
                      : modalMode === "add"
                      ? "Add"
                      : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="modal fade" id="deleteModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-body text-center p-4">
                <i className="ph ph-warning text-danger f-40 mb-3"></i>
                <h5>Are you sure?</h5>
                <p className="text-muted mb-4">This action cannot be undone.</p>
                <div className="d-flex justify-content-center gap-3">
                  <button
                    type="button"
                    className="btn btn-light-secondary"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-light-danger"
                    onClick={this.handleDelete}
                  >
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Carousels;
